import { Form, Formik, FormikValues } from "formik";
import ReKycAboutYou from "./ReKycAboutYou";
import { useOnboarding } from "src/store/features/onboarding.slice";
import ReKycNavigation from "./ReKycNavigation";
import { ReKycSfOpportunityPayloadDto } from "src/store/features/api/generated";
import { useUserSlice } from "src/store/features/user.slice";
import api from "src/store/features/api";
import LoadingComponent from "./LoadingComponent";
import ReKycApplicationConfig from "./utils/useReKycApplicationFormConfig";
import { useAppDispatch } from "src/store/store";
import RekycEntity from "./ReKycEntity";
import { trackEvent } from "src/Amplitude";

export default function ReKycApplicationForm() {
  const {
    firstName,
    lastName,
    cryptoJourney: { wealthSource, otherWealthSource },
    nationality,
    ssn,
    address: { country, state, zipCode, fullAddress, unitNumber, city, street },
    uploadedFiles,
    reKycApplicationStep,
    cryptoJourney: { occupation },
    registeredOfficeAddress: {
      registeredOfficeFullAddress,
      registeredOfficeCountry,
      registeredOfficeState,
      registeredOfficePostcode,
      registeredOfficeunitApartment,
      registeredOfficeCity,
    },
    placeOfBusinessAddress: {
      placeOfBusinessFullAddress,
      placeOfBusinessCountry,
      placeOfBusinessState,
      placeOfBusinessPostCode,
      placeOfBusinessunitApartment,
      placeOfBusinessCity,
    },
    reKycEntityId,
  } = useOnboarding();

  const {
    reKycDetails: { recordType, formSubmitted },
  } = useUserSlice();

  const [validationSchema] = ReKycApplicationConfig();

  const [submitForm, { isLoading }] = api.useRekycSalesForceOpportunityMutation();
  const dispatch = useAppDispatch();

  const initialValues = {
    firstname: firstName,
    lastname: lastName,
    occupation: occupation || "",
    ssn: ssn || "",
    nationality: nationality || "",
    country: country || "",
    address: fullAddress || "",
    state: state || "",
    city: city || "",
    street: street,
    postCode: zipCode || "",
    unitNumber: unitNumber || "",
    streetAddress: fullAddress?.split(",")[0] || "",
    unitApartment: unitNumber || "",
    wealthSource: wealthSource,
    otherWealthSource: otherWealthSource || "",
    registeredOfficeFullAddress: registeredOfficeFullAddress || "",
    registeredOfficeCountry: registeredOfficeCountry || "",
    registeredOfficeState: registeredOfficeState || "",
    registeredOfficePostcode: registeredOfficePostcode || "",
    registeredOfficeunitApartment: registeredOfficeunitApartment || "",
    registeredOfficeCity: registeredOfficeCity || "",
    registeredOfficeStreet: registeredOfficeFullAddress?.split(",")[0] || "",
    placeOfBusinessFullAddress: placeOfBusinessFullAddress || "",
    placeOfBusinessCountry: placeOfBusinessCountry || "",
    placeOfBusinessState: placeOfBusinessState || "",
    placeOfBusinessPostCode: placeOfBusinessPostCode || "",
    placeOfBusinessunitApartment: placeOfBusinessunitApartment || "",
    placeOfBusinessCity: placeOfBusinessCity || "",
    placeOfBusinessStreet: placeOfBusinessFullAddress?.split(",")[0] || "",
    reKycEntityId: reKycEntityId || "",
  };

  const handleSubmit = async (
    values: FormikValues,
    setFieldError: (field: string, message: string | undefined) => void,
  ) => {
    const {
      streetAddress,
      country,
      state,
      postCode: zipCode,
      registeredOfficeStreet,
      placeOfBusinessStreet,
    } = values;

    const countryToSubmit =
      country === "United States" ? "United States of America (USA)" : country;

    const entityAddress1countryToSubmit =
      registeredOfficeCountry === "United States"
        ? "United States of America (USA)"
        : registeredOfficeCountry;

    const entityAddress2countryToSubmit =
      placeOfBusinessCountry === "United States"
        ? "United States of America (USA)"
        : registeredOfficeCountry;

    const reKycSfOpportunityPayloadDto: ReKycSfOpportunityPayloadDto = {
      type: recordType,
      nationality,
      firstName,
      lastName,
      wealthSource,
      occupation,
      ssn,
      entityId: reKycEntityId,
      aboutYouAddress: {
        city: city || "",
        street: streetAddress?.split(",")[0],
        country: countryToSubmit,
        state,
        zipCode: zipCode,
      },
      entityAddress1: {
        city: registeredOfficeCity,
        street: registeredOfficeStreet?.split(",")[0],
        country: entityAddress1countryToSubmit,
        state: registeredOfficeState,
        zipCode: registeredOfficePostcode,
      },
      entityAddress2: {
        city: placeOfBusinessCity,
        street: placeOfBusinessStreet?.split(",")[0],
        country: entityAddress2countryToSubmit,
        state: placeOfBusinessState,
        zipCode: placeOfBusinessPostCode,
      },
      documents: uploadedFiles.map((file) => file.contentVersionId),
    };

    await submitForm({
      reKycSfOpportunityPayloadDto,
    })
      .then(() => {
        trackEvent("Re-KYC Application Submitted", { reKycSfOpportunityPayloadDto }, "Re-KYC");
      })
      .catch((error) => {
        window.alert(
          "An error occurred while submitting your Re-KYC application. Please try again later or contact C&B support.",
        );
        console.error(error);
      })
      .finally(() => {
        // Cancel tag causing to refetch of v2
        dispatch(api.util.invalidateTags(["getUserDataTag"]));
      });
  };

  return (
    <div>
      {isLoading ? (
        <LoadingComponent message={"Please wait while we submit your application."} />
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize={true}
          validateOnBlur={false}
          validateOnChange={false}
          onSubmit={(values, { setFieldError }) => handleSubmit(values, setFieldError)}>
          <Form>
            {reKycApplicationStep === "aboutYou" && !formSubmitted && <ReKycAboutYou />}
            {reKycApplicationStep === "aboutYourEntity" && !formSubmitted && <RekycEntity />}
            {!formSubmitted && <ReKycNavigation />}
          </Form>
        </Formik>
      )}
    </div>
  );
}
